import { createAsync } from '@solidjs/router';
import {
	AccessCircleLogo,
	ActivityIndicator,
	Button,
	CheckList,
	CheckListItem,
	Container,
	Dialog,
	Heading,
	Hero,
	HGroup,
	HorizontalRule,
	Link,
	MediaSection,
	MessageBar,
	Page,
	Picture,
	Section,
	Tabs,
	tierBenefit,
	tierLabel,
	tierPoints,
} from '@troon/ui';
import { For, Show, Suspense } from 'solid-js';
import { cachedGet } from '@troon/api-client';
import { IconSparkles } from '@troon/icons/sparkles';
import { IconUser01 } from '@troon/icons/user-01';
import { IconFlag } from '@troon/icons/flag';
import { IconTag } from '@troon/icons/tag';
import { FacilitiesByGrouping } from '../../../components/facility/by-grouping';
import { FacilityCard } from '../../../components/facility/card';
import { useUser } from '../../../providers/user';
import { AuthDialog } from '../../../partials/auth/dialog';
import { getConfigValue } from '../../../modules/config';
import { FrequentlyAskedQuestions } from '../../../components/faqs';
import type { RouteDefinition } from '@solidjs/router';

export default function LoyaltyPage() {
	const user = useUser();
	return (
		<>
			<Hero src={`${getConfigValue('IMAGE_HOST')}/loyalty/hero.jpg`} crop="bottom" straight>
				<div class="flex flex-col gap-8">
					<HGroup>
						<HGroup.Heading as="h1">Loyalty & Rewards</HGroup.Heading>
						<HGroup.Content size="h1" class="flex flex-col text-3xl">
							<span>Earn everywhere.</span>
							<span class="font-normal">Redeem for anything.</span>
						</HGroup.Content>
					</HGroup>

					<p class="text-sm md:text-base">
						Every round you play and every shop order you place earns Access Points into one unified balance. Use them
						for tee times, shop orders — anything across the network.
					</p>
					<Show when={!user()}>
						<AuthDialog key="auth-membership">
							<Dialog.ButtonTrigger class="mx-auto w-fit">Sign up for free</Dialog.ButtonTrigger>
						</AuthDialog>
					</Show>
					<div class="mx-auto -mb-16 max-w-full overflow-hidden rounded-t-4xl border-8 border-b-0 border-white backdrop-blur-lg md:rounded-t-[4rem] md:border-12 md:border-b-0">
						<Picture
							src={`${getConfigValue('IMAGE_HOST')}/loyalty/points-content.png`}
							width={1108}
							height={802}
							class="w-full max-w-lg"
							sizes="(min-width: 1024px) 24w, 85vw"
						/>
					</div>
				</div>
			</Hero>
			<Page class="pt-0">
				<Container>
					<Page>
						<Section class="grid gap-8 gap-x-16 text-sm text-balance lg:grid-cols-3">
							<div class="flex items-start gap-4">
								<div>
									<IconUser01 class="size-12 text-brand xl:size-16" />
								</div>
								<div>
									<Heading as="h2" size="h5">
										Sign Up For Free
									</Heading>
									<p>Create your Access account in seconds to start earning points —no cost to get started. </p>
								</div>
							</div>

							<div class="flex items-start gap-4">
								<div>
									<IconFlag class="size-12 text-brand xl:size-16" />
								</div>
								<div>
									<Heading as="h2" size="h5">
										Play, Shop, Earn
									</Heading>
									<p>
										Earn points on every tee time and every purchase in the Access Shop — points multiply as your status
										gro ws.
									</p>
								</div>
							</div>

							<div class="flex items-start gap-4">
								<div>
									<IconTag class="size-12 text-brand xl:size-16" />
								</div>
								<div>
									<Heading as="h2" size="h5">
										Redeem & Save
									</Heading>
									<p>
										Use your points to book tee times, save on gear, and unlock exclusive offers — on and off the
										course.
									</p>
								</div>
							</div>
						</Section>

						<HorizontalRule />

						<Section>
							<div class="flex flex-col items-center gap-12 text-center">
								<div class="flex flex-col items-center gap-4 text-center">
									<HGroup>
										<HGroup.Heading as="h2" color="brand">
											Status
										</HGroup.Heading>
										<HGroup.Content size="h2">
											More status. <span class="font-normal">More rewards.</span>
										</HGroup.Content>
									</HGroup>
									<p>
										Move through Access status levels to earn points faster, unlock better benefits, and get more value
										every time you play or shop.
									</p>
								</div>

								<ol class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
									<For each={Object.keys(tierLabel) as Array<keyof typeof tierLabel>}>
										{(tier) => (
											<li class="flex flex-col items-center gap-4 rounded-lg border border-neutral-300 bg-neutral-100 p-8 text-center">
												<AccessCircleLogo color={tier === 'member' ? 'brand' : tier} class="size-24" />
												<Heading as="h3" size="h6">
													{tierLabel[tier]}
													<Show when={tierPoints[tier]}>{(pts) => <> • {pts()}</>}</Show>
												</Heading>
												<p>{tierBenefit[tier]}</p>
											</li>
										)}
									</For>
								</ol>

								<div class="w-full text-start">
									<MessageBar appearance="access">
										<MessageBar.Icon>
											<IconSparkles />
										</MessageBar.Icon>
										<MessageBar.Content>
											<p>Unlock automatic status and savings when you upgrade to Access Premium.</p>
										</MessageBar.Content>
										<MessageBar.Action as={Link} href="/membership">
											Learn more
										</MessageBar.Action>
									</MessageBar>
								</div>
							</div>
						</Section>

						<HorizontalRule />

						<Section>
							<div class="flex flex-col gap-4">
								<HGroup>
									<HGroup.Heading as="h2" color="brand">
										Play
									</HGroup.Heading>
									<HGroup.Content size="h2">Earn every time you play.</HGroup.Content>
								</HGroup>
								<p>
									Every round earns you Access Points. Use them to pay for tee times at any of courses in the network.
								</p>
							</div>

							<Tabs defaultValue="featured" lazyMount>
								<Tabs.List aria-label="Access course lists">
									<Tabs.Tab value="featured">Featured Courses</Tabs.Tab>
									<Tabs.Tab value="nearby">Nearby Courses</Tabs.Tab>
									<Tabs.Tab value="all">All Courses</Tabs.Tab>
								</Tabs.List>

								<Tabs.Panel value="featured">
									<Facilities type="featured" />
								</Tabs.Panel>
								<Tabs.Panel value="nearby">
									<Facilities type="nearby" />
								</Tabs.Panel>
								<Tabs.Panel value="all">
									<AllFacilities />
								</Tabs.Panel>
							</Tabs>
						</Section>

						<HorizontalRule />

						<MediaSection src={`${getConfigValue('IMAGE_HOST')}/membership/shop.png`} aspectRatio="square">
							<div class="flex flex-col gap-4">
								<HGroup>
									<HGroup.Heading as="h2" color="brand">
										Shop
									</HGroup.Heading>
									<HGroup.Content size="h2">Earn every time you shop.</HGroup.Content>
								</HGroup>
								<p>
									Shop your favorite brands in golf from the Access Shop — and let your points work in both directions.
								</p>
								<CheckList>
									<CheckListItem>Use your points for tee times or shop credit</CheckListItem>
									<CheckListItem>Top brands: TaylorMade, Callaway & more</CheckListItem>
								</CheckList>
								<Button as={Link} appearance="tertiary-current" href="https://shop.troon.com" class="w-fit">
									Visit the shop
								</Button>
							</div>
						</MediaSection>

						<HorizontalRule />

						<Section>
							<Container size="medium" class="text-center">
								<Heading as="h2" size="h2" class="text-center">
									Frequently Asked Questions
								</Heading>
								<p class="text-lg">
									From Arizona to the Carolinas, play the best courses in the country at members-only rates.
								</p>
								<div class="overflow-hidden rounded-lg border border-neutral">
									<FrequentlyAskedQuestions id="membership-faqs" />
								</div>
							</Container>
						</Section>
					</Page>
				</Container>
				<Section appearance="primary" class="bg-linear-90 from-neutral-950 to-brand-600">
					<Container class="flex flex-col items-center gap-8 text-center">
						<Heading as="h2" size="h1">
							Ready to start earning?
						</Heading>
						<p>Supporting text that provides additional context about the page or section.</p>
						<AuthDialog key="auth-membership">
							<Dialog.ButtonTrigger class="w-fit">Sign up for free</Dialog.ButtonTrigger>
						</AuthDialog>
					</Container>
				</Section>
			</Page>
		</>
	);
}

function Facilities(props: { type: 'featured' | 'nearby' }) {
	const facilities = createAsync(
		async () => {
			const query =
				props.type === 'featured'
					? { featuredOnGolfWithAccess: true, supportsAccessPoints: true, limit: 3 }
					: { nearby: true, supportsAccessPoints: true, limit: 3 };
			const res = await getFacilities({ query });
			return res.facilities;
		},
		{ deferStream: false },
	);

	return (
		<div class="grid gap-8 md:grid-cols-3">
			<Suspense
				fallback={
					<div class="col-span-3 grid h-32 place-items-center">
						<ActivityIndicator />
					</div>
				}
			>
				<For
					each={facilities()}
					fallback={
						<p class="col-span-3 py-16 text-center">Sorry, we do not have any courses near you at this time.</p>
					}
				>
					{(facility) => <FacilityCard facility={facility} level="h3" loading="lazy" linkToTeeTimes disableFavorites />}
				</For>
			</Suspense>
		</div>
	);
}

function AllFacilities() {
	const facilities = createAsync(
		async () => {
			const all = await getFacilities({ query: { isBookable: true, sortBy: 'name', supportsAccessPoints: true } });

			return all.facilities;
		},
		{ deferStream: true },
	);

	return (
		<Suspense
			fallback={
				<div class="col-span-3 grid h-32 place-items-center">
					<ActivityIndicator />
				</div>
			}
		>
			<Show when={facilities()}>{(facilities) => <FacilitiesByGrouping facilities={facilities()} />}</Show>
		</Suspense>
	);
}

const getFacilities = cachedGet('/v0/facilities');

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
